import * as _ from 'lodash';
import moment from 'moment';
import type { ReactNode } from 'react';
import { Pager } from 'react-bootstrap';

import { useSelector } from 'react-redux';
import type { APIError, Project, Repository } from '../../../types';
import Icon, { icons, IconText } from '../../generic/Icon';
import Link, { SupportLink } from '../../generic/Link';
import Loader from '../../generic/Loader';
import BranchDropdown from './BranchDropdown';
import Button from '../../generic/Button';
import ConnectToServiceFallback from './Fallbacks/ConnectToServiceFallback';
import useIsGithubApp from './hooks/useIsGithubApp';
import VCSModule from '../../../redux/vcs';
import InstallGithubAppFallback from './Fallbacks/InstallGithubAppFallback';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { HYBRID_PROJECTS } from '../../../../shared/featureFlags/flagNames';
import HybridProjectsTable from './HybridProjectsTable';

export type OnBranchChangeFn = (repo: Repository, newBranch: string) => void;
export type OnRowRedirectFn = (projectId: string) => void;
export type OnSelectRepositoryFn = (row: Repository) => void;

type ServiceImportRespositoriesProps = {
  repositories: Repository[];
  selectedRepos: Repository[];
  page: number;
  pageSize: number;
  isConnected: boolean;
  isLoading: boolean;
  error?: APIError | string;
  onPageUp: () => Promise<void>;
  onPageDown: () => Promise<void>;
  onBranchChange: OnBranchChangeFn;
  onRowRedirect: OnRowRedirectFn;
  onSelectRepository: OnSelectRepositoryFn;
  onConnectToService: () => void;
  service: Project['vcs_host'] | 'slack';
};

const EmptyRepoPanel = ({ children }: { children: ReactNode }) => (
  <div className="panel text-center empty-repo-panel">
    {children}
  </div>
);

const ServiceImportRespositories = ({
  isConnected,
  isLoading,
  error,
  repositories,
  selectedRepos,
  page,
  pageSize,
  onPageDown,
  onPageUp,
  onBranchChange,
  onRowRedirect,
  onSelectRepository,
  onConnectToService,
}: ServiceImportRespositoriesProps) => {
  const filters = useSelector(VCSModule.selectors.getFilters);
  const isGithubApp = useIsGithubApp();
  const areHybridProjectsActive = useIsFeatureEnabled(HYBRID_PROJECTS);

  let repoPanelContent;
  if (!isConnected) {
    repoPanelContent = isGithubApp ? (
      <ConnectToServiceFallback onConnectToService={onConnectToService} />
    ) : (
      <p>Connect to instance to see repos</p>
    );
  } else if (!filters.length && isGithubApp) {
    repoPanelContent = <InstallGithubAppFallback />;
  } else if (isLoading) {
    repoPanelContent = <Loader />;
  } else if (error) {
    repoPanelContent = <p>{`Error loading repositories: ${error}`}</p>;
  } else if (!repositories.length && page > 1) {
    repoPanelContent = (
      <>
        <h2>No repositories found</h2>
        <p>Oops! Looks like that was the last page.</p>
        <p>
          <Button onClick={onPageDown}>Click here to go back.</Button>
        </p>
      </>
    );
  } else if (!repositories.length) {
    repoPanelContent = (
      <>
        <h2>No repositories found</h2>
        <div>
          We could not find any repositories in this account. Please <SupportLink className="highlight">contact support</SupportLink> for assistance.
        </div>
      </>
    );
  }

  if (repoPanelContent) {
    return <EmptyRepoPanel>{repoPanelContent}</EmptyRepoPanel>;
  }

  if (isGithubApp && areHybridProjectsActive) {
    return (
      <>
        <HybridProjectsTable
          onBranchChange={onBranchChange}
          repositories={repositories}
          selectedRepos={selectedRepos}
          onSelectRepository={onSelectRepository}
        />
        <div className="text-center">
          <Pager>
            {page > 1 && (
              <Pager.Item previous disabled={page <= 1} onClick={onPageDown}>
                &larr; Previous
              </Pager.Item>
            )}
            <Pager.Item disabled>
              <b>Page {page}</b>
            </Pager.Item>
            {repositories.length >= pageSize && (
              <Pager.Item
                next
                disabled={repositories.length < pageSize}
                onClick={onPageUp}>
                Next &rarr;
              </Pager.Item>
            )}
          </Pager>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="panel" style={{ overflow: 'visible' }}>
        <table className="table project-table table-striped">
          <thead>
            <tr>
              <th />
              <th>Title</th>
              <th>Branch</th>
              <th>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {repositories.map((row, i) =>
              <RepositoryRow
                key={row.id || i}
                row={row}
                selectedRepos={selectedRepos}
                onBranchChange={onBranchChange}
                onRowRedirect={onRowRedirect}
                onSelectRepository={onSelectRepository}
              />)}
          </tbody>
        </table>
      </div>
      <div className="text-center">
        <Pager>
          {page > 1 && (
            <Pager.Item previous disabled={page <= 1} onClick={onPageDown}>
              &larr; Previous
            </Pager.Item>
          )}
          <Pager.Item disabled>
            <b>Page {page}</b>
          </Pager.Item>
          {repositories.length >= pageSize && (
            <Pager.Item
              next
              disabled={repositories.length < pageSize}
              onClick={onPageUp}>
              Next &rarr;
            </Pager.Item>
          )}
        </Pager>
      </div>
    </>);
};

type RepositoryRowProps = {
  row: Repository;
  selectedRepos: Repository[];
  onBranchChange: OnBranchChangeFn;
  onRowRedirect: OnRowRedirectFn;
  onSelectRepository: OnSelectRepositoryFn;
};

const RepositoryRow = ({
  row,
  selectedRepos,
  onBranchChange,
  onRowRedirect,
  onSelectRepository,
}: RepositoryRowProps) => {
  const isImported = !!row.project;
  const hasNoBranches = !row.branches || _.isEmpty(row.branches);
  const isSelected = !!selectedRepos.find(repo => repo.locator === row.locator);

  let onRowClick: () => void;
  if (isImported) {
    onRowClick = () => onRowRedirect(row.project?.locator || '');
  } else if (hasNoBranches) {
    onRowClick = _.noop;
  } else {
    onRowClick = () => onSelectRepository(row);
  }

  return (
    <tr
      className={
        (isSelected ? 'selected ' : '') +
        (!isImported ? '' : 'row-success ') +
        (hasNoBranches ? ' row-disabled' : '')
      }
      onClick={(e) => {
        // Opening the branch dropdown calls e.preventDefault(). We
        // catch that here to avoid selecting the current repository.
        if (e.defaultPrevented) {
          return;
        }

        onRowClick();
      }}
    >
      <td className="text-center">
        {!hasNoBranches && (
          isImported ? (
            <Icon icon={icons.externalLink} />
          ) : (
            <input
              type="checkbox"
              checked={isSelected}
            />
          )
        )}
      </td>
      <td>
        <span className="margin-right-xs">
          {row.isFork && <Icon icon={icons.codeFork} />}
        </span>
        <Link
          to={row.url}
          openInNewTab
        >
          <b>{row.title}</b>
        </Link>
          &nbsp;&nbsp;
        {row.isPrivate
          && (
            <span className="badge">
              <IconText
                icon={icons.lock}
                text="Private"
              />
            </span>
          )}
      </td>
      <td>
        { isImported
          ? (
            <small>
              <IconText
                icon={icons.checkCircle}
                text={`Imported: ${moment(row.project?.createdAt).fromNow()}`}
              />
            </small>
            )
          : (!hasNoBranches && <BranchDropdown onBranchChange={onBranchChange} repo={row} />)}
      </td>
      <td>
        {row.updated ? <i>({moment(row.updated).fromNow()})</i> : ''}
      </td>
    </tr>
  );
};

export default ServiceImportRespositories;
